import i18n from '@/lib/i18n';
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthService, type LoginRequest, type RegisterRequest, type ChangePasswordRequest } from '@/lib/api';
import { queryKeys, invalidateQueries } from '@/lib/queryClient';
import { tokenStorage } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { toast } from 'react-hot-toast';
import type { LoginForm } from '@/types';

// Login mutation
export const useLogin = () => {
  const queryClient = useQueryClient();
  const { login } = useAuthStore();

  return useMutation({
    mutationFn: (credentials: LoginForm) => login(credentials),
    onSuccess: () => {
      // Success is handled in the auth store login method
      // No need to duplicate logic here
      toast.success(i18n.t('auth.toast.loggedIn'));
    },
    onError: (error: any) => {
      const message = error?.message || 'Login failed. Please check your credentials.';
      toast.error(message);
    },
  });
};

// Logout mutation
export const useLogout = () => {
  const queryClient = useQueryClient();
  const { clearAuth } = useAuthStore();

  return useMutation({
    mutationFn: () => AuthService.logout(),
    onSuccess: () => {
      // Clear tokens
      tokenStorage.clearTokens();
      
      // Clear auth store
      clearAuth();
      
      // Clear all cached data
      queryClient.clear();
      
      toast.success(i18n.t('auth.toast.loggedOut'));
    },
    onError: () => {
      // Even if logout fails on server, clear local data
      tokenStorage.clearTokens();
      clearAuth();
      queryClient.clear();
    },
  });
};

// Register mutation
export const useRegister = () => {
  const queryClient = useQueryClient();
  const { setUser, setTokens } = useAuthStore();

  return useMutation({
    mutationFn: (userData: RegisterRequest) => AuthService.register(userData),
    onSuccess: (data) => {
      // Store tokens
      tokenStorage.setTokens(data.accessToken, data.refreshToken);
      
      // Update auth store
      setUser(data.user);
      setTokens(data.accessToken, data.refreshToken);
      
      // Cache user profile
      queryClient.setQueryData(queryKeys.users.profile(), data.user);
      
      // Invalidate users list to include new user
      invalidateQueries.users.lists();
      
      toast.success(i18n.t('auth.toast.accountCreated'));
    },
    onError: (error: any) => {
      const message = error?.message || 'Registration failed. Please try again.';
      toast.error(message);
    },
  });
};

// Change password mutation
export const useChangePassword = () => {
  return useMutation({
    mutationFn: (passwords: ChangePasswordRequest) => AuthService.changePassword(passwords),
    onSuccess: () => {
      toast.success(i18n.t('auth.toast.passwordChanged'));
    },
    onError: (error: any) => {
      const message = error?.message || 'Failed to change password. Please try again.';
      toast.error(message);
    },
  });
};

/*
 * useForgotPassword and useResetPassword are gone along with the client methods
 * they wrapped. They posted to /auth/forgot-password and /auth/reset-password,
 * which the backend has never implemented, so both could only ever fail — and a
 * self-service reset arrives by email, which is not a channel anyone in this
 * market reads.
 *
 * Recovery is a person with an endpoint that works: an owner resets their
 * staff's passwords from their own panel, and the super admin resets an
 * owner's from the platform panel.
 */

// Validate token query
export const useValidateToken = () => {
  return useQuery({
    queryKey: queryKeys.auth.validate(),
    queryFn: () => AuthService.validateToken(),
    enabled: !!tokenStorage.getAccessToken(),
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Auto-login hook for app initialization
export const useAutoLogin = () => {
  const { user, checkAuth, isLoading } = useAuthStore();
  
  // Use the store's checkAuth method instead of a separate query
  React.useEffect(() => {
    // Only run checkAuth if we don't already have a user and we're not already loading
    if (!user && !isLoading) {
      console.log('useAutoLogin: Calling checkAuth...');
      checkAuth().catch((error) => {
        console.log('useAutoLogin: checkAuth failed:', error.message);
      });
    }
  }, [user, checkAuth, isLoading]);

  // Return a mock query object for compatibility
  return {
    data: user,
    isLoading,
    isError: false,
    error: null,
  };
};

// Check if user has specific role
export const useHasRole = (requiredRoles: string | string[]) => {
  const { user } = useAuthStore();
  
  if (!user) return false;
  
  const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
  return roles.includes(user.role);
};

// Check if user has specific permission
export const useHasPermission = (requiredPermissions: string | string[]) => {
  const { user } = useAuthStore();
  
  if (!user) return false;
  
  const permissions = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];
  // user.permissions is a RolePermissions object, not an array — calling
  // .includes() on it would have thrown rather than returned false. The list of
  // permission strings lives one level in.
  const granted = user.permissions?.permissions ?? [];
  return permissions.some(permission => granted.includes(permission));
};

// Get current user
export const useCurrentUser = () => {
  const { user } = useAuthStore();
  return user;
};

// Check if user is authenticated
export const useIsAuthenticated = () => {
  const { user } = useAuthStore();
  return !!user && tokenStorage.hasValidToken();
};